// Inspecteur — Bedrijfsmiddel / Bedreiging / Risico DETAIL drill-in (design-vs-build gap, Batch 1).
// The registers were list-only; these pages let a user open an entity and see its linked context across
// the asset↔threat↔risk triad: an asset shows the threats it faces + the risks referencing it; a threat
// shows the assets it applies to + its risks; a risk shows its Verwerking×Bedreiging×Bedrijfsmiddel +
// inherent/residual scores. Backend: GET /{bedrijfsmiddelen|bedreigingen|risico}/:id/detail. RLS-scoped.
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ApiError } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft } from "lucide-react";

type Row = Record<string, unknown>;
const errText = (e: unknown) => (e instanceof ApiError ? `${e.status}` : (e as Error).message);
const s = (v: unknown) => (v == null || v === "" ? "—" : String(v));
const scale = (l: unknown, i: unknown) => (l == null ? "—" : `${String(l)} × ${String(i ?? "?")}`);

/** Shared shell: back link + header + loading/error, then the per-entity body. */
function DetailShell({ backLabel, title, sub, q, children }: {
  backLabel: string; title: string; sub?: string; q: { isLoading: boolean; error: unknown; data: unknown }; children: (d: Row) => React.ReactNode;
}) {
  const { t } = useI18n();
  const d = q.data as Row | undefined;
  return (
    <section>
      <Link to="/risico" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="size-4" /> {backLabel}
      </Link>
      {q.isLoading ? <div className="space-y-3"><Skeleton className="h-9 w-2/3" /><Skeleton className="h-40 w-full" /></div>
        : q.error ? <p className="text-sm text-destructive">{t("crud.failed")} {errText(q.error)}</p>
        : !d ? <p className="text-sm text-muted-foreground">{t("activity.detail.notFound")}</p>
        : <><PageHeader title={title} {...(sub ? { description: sub } : {})} /><div className="grid gap-4">{children(d)}</div></>}
    </section>
  );
}

/** A panel listing linked entities, each a link to its own detail. */
function LinkedPanel({ title, rows, to, empty }: { title: string; rows: Row[]; to: "/bedrijfsmiddelen/$id" | "/bedreigingen/$id"; empty: string }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        {rows.length === 0 ? <p className="text-sm text-muted-foreground">{empty}</p> : (
          <ul className="m-0 list-none p-0">
            {rows.map((r) => (
              <li key={String(r.id)} className="flex items-center justify-between gap-3 border-b py-2 text-sm last:border-b-0">
                <Link to={to} params={{ id: String(r.id) }} className="font-medium text-primary hover:underline">{s(r.name)}</Link>
                {Boolean(r.threatClass ?? r.assetClass) && <Badge variant="secondary">{s(r.threatClass ?? r.assetClass)}</Badge>}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** A panel listing the risks (risico rows) referencing this entity. */
function RisksPanel({ rows, otherKey, otherLabel }: { rows: Row[]; otherKey: string; otherLabel: string }) {
  const { t } = useI18n();
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{t("risk.detail.risks")}</CardTitle></CardHeader>
      <CardContent>
        {rows.length === 0 ? <p className="text-sm text-muted-foreground">{t("risk.detail.noRisks")}</p> : (
          <ul className="m-0 list-none p-0">
            {rows.map((r) => (
              <li key={String(r.id)} className="flex items-center justify-between gap-3 border-b py-2 text-sm last:border-b-0">
                <span className="min-w-0 truncate"><span className="font-medium">{s(r.verwerking)}</span> <span className="text-muted-foreground">· {otherLabel}: {s(r[otherKey])}</span></span>
                <span className="flex shrink-0 items-center gap-2">
                  <Badge variant="outline" className="font-mono">{scale(r.inherentLikelihood, r.inherentImpact)}</Badge>
                  {Boolean(r.treatmentDecision) && <Badge variant="secondary" className="capitalize">{s(r.treatmentDecision)}</Badge>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function Fields({ items }: { items: Array<[string, unknown]> }) {
  return (
    <Card><CardContent className="grid gap-2 sm:grid-cols-2">
      {items.map(([k, v]) => (
        <div key={k} className="flex flex-col gap-0.5">
          <span className="text-xs text-muted-foreground">{k}</span>
          <span className="text-sm">{s(v)}</span>
        </div>
      ))}
    </CardContent></Card>
  );
}

export function AssetDetailRoute() {
  const { t } = useI18n();
  const { id } = useParams({ from: "/bedrijfsmiddelen/$id" });
  const q = useQuery({ queryKey: ["asset-detail", id], queryFn: () => api.getAssetDetail(id) as Promise<{ asset: Row; threats: Row[]; risks: Row[] }> });
  return (
    <DetailShell backLabel={t("risk.detail.backAssets")} title={s((q.data as { asset?: Row } | undefined)?.asset?.name)} q={q as never}>
      {(d) => { const a = d.asset as Row; return (<>
        <Fields items={[[t("risk.f.category"), a.category], [t("risk.f.assetClass"), a.assetClass], [t("risk.f.vulnerability"), a.vulnerability], [t("risk.f.likelihood"), a.likelihood], [t("risk.f.description"), a.description]]} />
        <LinkedPanel title={t("risk.detail.threatsFaced")} rows={(d.threats as Row[]) ?? []} to="/bedreigingen/$id" empty={t("risk.detail.noThreats")} />
        <RisksPanel rows={(d.risks as Row[]) ?? []} otherKey="bedreiging" otherLabel={t("risk.f.threat")} />
      </>); }}
    </DetailShell>
  );
}

export function ThreatDetailRoute() {
  const { t } = useI18n();
  const { id } = useParams({ from: "/bedreigingen/$id" });
  const q = useQuery({ queryKey: ["threat-detail", id], queryFn: () => api.getThreatDetail(id) as Promise<{ threat: Row; assets: Row[]; risks: Row[] }> });
  return (
    <DetailShell backLabel={t("risk.detail.backThreats")} title={s((q.data as { threat?: Row } | undefined)?.threat?.name)} q={q as never}>
      {(d) => { const th = d.threat as Row; return (<>
        <Fields items={[[t("risk.f.category"), th.category], [t("risk.f.threatClass"), th.threatClass], [t("risk.f.probability"), th.probability], [t("risk.f.description"), th.description]]} />
        <LinkedPanel title={t("risk.detail.assetsAffected")} rows={(d.assets as Row[]) ?? []} to="/bedrijfsmiddelen/$id" empty={t("risk.detail.noAssets")} />
        <RisksPanel rows={(d.risks as Row[]) ?? []} otherKey="bedrijfsmiddel" otherLabel={t("risk.f.asset")} />
      </>); }}
    </DetailShell>
  );
}

export function RisicoDetailRoute() {
  const { t } = useI18n();
  const { id } = useParams({ from: "/risico/$id" });
  const q = useQuery({ queryKey: ["risico-detail", id], queryFn: () => api.getRisicoDetail(id) as Promise<Row> });
  const d = q.data as Row | undefined;
  const title = d ? `${s(d.bedreiging)} × ${s(d.bedrijfsmiddel)}` : t("page.risico");
  return (
    <DetailShell backLabel={t("risk.detail.backRisks")} title={title} q={q as never}>
      {(r) => (<>
        <Fields items={[[t("risk.f.verwerking"), r.verwerking], [t("risk.f.threat"), r.bedreiging], [t("risk.f.asset"), r.bedrijfsmiddel], [t("risk.detail.inherent"), scale(r.inherentLikelihood, r.inherentImpact)], [t("risk.detail.residual"), r.residualLikelihood ? scale(r.residualLikelihood, r.residualImpact) : "—"], [t("risk.f.treatment"), r.treatmentDecision], [t("risk.f.description"), r.description]]} />
        <Card><CardContent className="flex flex-wrap gap-2 pt-6">
          {Boolean(r.bedreigingId) && <Link to="/bedreigingen/$id" params={{ id: String(r.bedreigingId) }} className="text-sm text-primary hover:underline">→ {t("risk.f.threat")}: {s(r.bedreiging)}</Link>}
          {Boolean(r.bedrijfsmiddelId) && <Link to="/bedrijfsmiddelen/$id" params={{ id: String(r.bedrijfsmiddelId) }} className="text-sm text-primary hover:underline">→ {t("risk.f.asset")}: {s(r.bedrijfsmiddel)}</Link>}
        </CardContent></Card>
      </>)}
    </DetailShell>
  );
}
